import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { BillEstimateOrderItemDto } from '@css-xp/common-services/src/api/gateways/ccp-gateways/src/ccp-api-order';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { formatToUSD } from '../../utils/currency-display';
import Button from '@atlaskit/button';
import classNames from 'classnames';

const renderPrimitiveTooltip = () => {
  return styled(TooltipPrimitive)`
    background: #172b4d;
    border-radius: 3px;
    color: #ffffff;
    padding: 10px;
    font-weight: 500;
    font-size: 12px;
    line-height: 14px;
  `;
};

const UnavailableRow = ({ label }: { label: string }) => (
  <>
    <div className="price-grid-item price-grid-item__label">{label}</div>
    <div className="price-grid-item price-grid-item__value">
      unavailable
      <span className="unavailable-tooltip" data-testid="unavailable-tooltip">
        <Tooltip
          content={
            <>
              <div>Discounts, credit and taxes aren’t included in this price estimate, but</div>
              <div>can be found on the next invoice.</div>
            </>
          }
          position="bottom-end"
          tag="span"
          component={renderPrimitiveTooltip()}
        >
          <EditorPanelIcon label="price-tooltip-info" />
        </Tooltip>
      </span>
    </div>
  </>
);

type Props = {
  isLoading: boolean;
  billEstimate: BillEstimateOrderItemDto | null;
  linkedEntitlementsBillEstimate?: BillEstimateOrderItemDto[];
  currentEstimatedBill: number;
  setGrandTotal?: (value: number) => void;
  renderDetailsAccordion: boolean;
};

export const GlpPriceDifferenceContent = ({
  isLoading,
  billEstimate,
  linkedEntitlementsBillEstimate = [],
  currentEstimatedBill,
  setGrandTotal,
  renderDetailsAccordion,
}: Props) => {
  const [showDetails, setShowDetails] = useState<boolean>(false); // When view details link is given keep the details section collapsed.
  const grandTotal = useMemo(() => {
    let total = 0;
    if (billEstimate?.saleTransitionDetails?.glpForNextPlan) {
      total = billEstimate.saleTransitionDetails.glpForNextPlan;
    }
    linkedEntitlementsBillEstimate.forEach(({ saleTransitionDetails }) => {
      if (saleTransitionDetails?.glpForNextPlan) {
        total += saleTransitionDetails?.glpForNextPlan;
      }
    });
    if (setGrandTotal) {
      setGrandTotal(total / 100);
    }
    return formatToUSD(total / 100);
  }, [billEstimate, linkedEntitlementsBillEstimate]);

  if (isLoading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader classNameInner="estimate-loader" />
        <br />
        <SkeletonLoader classNameInner="estimate-loader" />
      </SkeletonLoaderWrapper>
    );
  }

  return (
    <>
      {!renderDetailsAccordion && (
        <p className="old-price" aria-label="Current grand total of bill estimate">
          {formatToUSD(currentEstimatedBill ?? 0)}
        </p>
      )}
      <p data-testid="new-price" aria-label="Updated grand total of bill estimate">
        {grandTotal}
      </p>
      <div
        className={classNames('price-grid', {
          'collapsed-accordion': renderDetailsAccordion,
          'expanded-accordion': showDetails,
        })}
      >
        <div className="price-grid-item price-grid-item__label">Subtotal</div>
        <div className="price-grid-item price-grid-item__value">{grandTotal}</div>
        {['Credits', 'Discounts', 'Taxes'].map((label) => (
          <UnavailableRow label={label} key={label} />
        ))}
      </div>
      {renderDetailsAccordion && (
        <Button
          spacing="none"
          appearance="link"
          testId="bill-estimate-details"
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(!showDetails);
          }}
        >
          View details
        </Button>
      )}
    </>
  );
};
